import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, User, Mail, BookOpen, Loader2, GraduationCap, Briefcase, DollarSign, History, AlertCircle, Info, Edit, UserCheck, MessageCircle, FileEdit, Package, PlusCircle, Lock, LockOpen, MoreHorizontal, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';
import { formatDate, formatDateTime as formatDateTimeCentral } from '../../../lib/dateFormat';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import TaskList from '../../../components/tasks/TaskList';
import TaskCount from '../../../components/tasks/TaskCount';
import WizardShell from '../../../components/ui/wizard-shell';
import { Progress } from '../../../components/ui/progress';
import { api } from '../../../lib/api';
import { personTypesApi, type PersonType } from '../../../lib/person-types';
import { toast } from 'sonner';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import SubProfileForm from '../../../components/person/SubProfileForm';
import { useStandort } from '../../../components/layouts/use-standort';
import KuendigungWizardModal from '../../../components/kuendigungen/KuendigungWizardModal';
import PersonCollapsibleSection from '../../../components/person/detail/PersonCollapsibleSection';
import VertragsKacheln from '../../../components/person/detail/VertragsKacheln';
import MessageWizardModal from '../../../components/communications/MessageWizardModal';
import { useAnrufen } from '../../../hooks/useAnrufen';
import { Switch } from '../../../components/ui/switch';
import { personenApi, type TeacherAvailabilitySlot } from '../../../lib/personen';
import { personenAccessApi } from '../../../lib/api/personen';
import { useGeoapifyAddressAutocomplete } from '../../../hooks/useGeoapifyAddressAutocomplete';
import {
  derivePrimaryEmailFromChannels,
  type PersonCommunicationChannel,
  usePersonCommunicationChannels,
} from '../../../hooks/usePersonCommunicationChannels';
import PersonDetailsWizardModal from '../../../components/person/PersonDetailsWizardModal';
import PersonDetailHeader from '../../../components/person/detail/PersonDetailHeader';
import PersonDetailTabs from '../../../components/person/detail/PersonDetailTabs';
import PersonAssociatedPersonsTab from '../../../components/person/detail/tabs/PersonAssociatedPersonsTab';
import PersonTeacherTab from '../../../components/person/detail/tabs/PersonTeacherTab';
import PersonOverviewTab from '../../../components/person/detail/tabs/PersonOverviewTab';
import PaymentHistoryTab from '../../../components/person/detail/tabs/PaymentHistoryTab';
import ContractAccountsSection from '../../../components/person/detail/ContractAccountsSection';
import EinmalcodesSection from '../../../components/person/detail/EinmalcodesSection';
import { resolvePeopleBasePath } from '../../../lib/peopleRoutes';
import HistoryCorrectionDialog, { type KorrigierbarerEintrag } from '../../../components/person/detail/HistoryCorrectionDialog';
import PersonZusammenfuehrenDialog from '../../../components/person/PersonZusammenfuehrenDialog';
import { openTeacherInvoiceDocument } from '../../../lib/api/teacherInvoices';

// Entpackt paginierte {items}-Antworten defensiv zu einem Array, damit ein
// {items,total}-Wrapper nicht zu "x.map is not a function" führt.
function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown }).items)) {
    return (value as { items: T[] }).items;
  }
  return [];
}

// Typen für Personen
interface Person {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  companyName?: string | null;
  isCompany?: boolean;
  email: string;
  phone?: string;
  // „none“: noch ohne Rolle und ohne Vertrag (T-0297).
  type: 'student' | 'teacher' | 'both' | 'customer' | 'none';
  status: 'active' | 'inactive' | 'pending' | 'locked';
  accessLocked?: boolean;
  instrument?: string;
  joinDate: string;
  birthDate?: string;
  address?: string;
  notes?: string;
  wpUserId?: string;
  parentId?: string;
  subProfiles?: string[];
  availability?: string;
  hourlyRate?: string;
  qualifications?: string;
  salutation?: 'Herr' | 'Frau' | 'Divers' | 'Firma';
  newsletter?: boolean;
  structuredAddress?: Record<string, unknown>;
  communications?: PersonCommunicationChannel[];
  communicationChannels?: PersonCommunicationChannel[];
  teacher?: {
    shortCode?: string | null;
    taxNumber?: string | null;
    bank?: { accountHolder?: string | null; iban?: string | null } | null;
    subjects?: number[] | null;
  };
  hasWpUser?: boolean;
  onboardingCompleted?: boolean;
}

type CommunicationEntry = PersonCommunicationChannel;
// Kontingent Typen
interface Contingent {
  id: number | string;
  name: string;
  totalUnits: number;
  remainingUnits: number;
  description?: string;
  purchasedAt?: string;
}
interface CustomerContract {
  enrollmentId: number;
  enrollmentStatus: string;
  subprofileId: string;
  startDate: string | null;
  startTime: string | null;
  weekday: number | null;
  durationMin: number | null;
  unterrichtsformName: string | null;
  teacherName: string | null;
  orderId: number | null;
  orderStatus: string | null;
  orderCreatedAt: string | null;
  hasContractPdf: boolean;
  packageName: string | null;
  totalUnits: number | null;
  remainingUnits: number | null;
  usedUnits: number | null;
}
// Konfigurierbare Kontingent-Pakete (via API zu laden)
// Historie
interface HistoryEntry {
  id: string;
  date: string;
  type: string;
  description: string;
  user: string;
  createdAt?: string;
  actorEmail?: string;
  /** VA-18 / ANG-3: optionaler mehrzeiliger Detail-Text (Vorlagen-gefüllt). */
  details?: string | null;
  /** Korrektur als neue Version (T-0077). */
  correctsId?: number | null;
  correctedById?: number | null;
  correctionReason?: string | null;
}
type TeacherPayload = {
  shortCode?: unknown;
  taxNumber?: unknown;
  bank?: {
    accountHolder?: unknown;
    iban?: unknown;
  };
  subjects?: unknown[];
  artRates?: Array<Record<string, unknown>>;
  portraitUrl?: unknown;
  portraitAlt?: unknown;
  publicBio?: unknown;
};

const toTeacherPayload = (value: unknown): TeacherPayload => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  return value as TeacherPayload;
};

const normalizeCommunicationChannels = (value: unknown): CommunicationEntry[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    .map((entry) => ({
      id: typeof entry.id === 'number' ? entry.id : undefined,
      type: String(entry.type ?? ''),
      value: String(entry.value ?? ''),
      isPrimary: entry.isPrimary === true || entry.is_primary === 1,
      officeContactAllowed: entry.officeContactAllowed !== false && entry.office_contact_allowed !== 0,
      isActive: entry.isActive !== false && entry.is_active !== 0,
      source: typeof entry.source === 'string' ? entry.source : undefined,
    }));
};

const toStructuredAddress = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  return value as Record<string, unknown>;
};
// (Mock entfernt)
const PersonDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const peopleListPath = `${resolvePeopleBasePath(location.pathname)}/people`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [korrekturFuer, setKorrekturFuer] = useState<KorrigierbarerEintrag | null>(null);
  const historieNeuLaden = async () => {
    try {
      const hist = await api.get<HistoryEntry[]>(`/api/personen/${id}/history`);
      setHistory(asArray<HistoryEntry>(hist));
    } catch {
      // Die alte Liste bleibt stehen.
    }
  };
  const [contingents, setContingents] = useState<Contingent[]>([]);
  // Endgültiges Löschen verlangt einen Grund (RBAC D.8) — beide Fälle laufen über einen Dialog.
  const [zuLoeschen, setZuLoeschen] = useState<
    { art: 'profil'; index: number; name: string } | { art: 'kontingent'; kontingent: Contingent } | null
  >(null);
  const [contracts, setContracts] = useState<CustomerContract[]>([]);
  const contingentLedgerReadOnly = true;
  const [isEditing, setIsEditing] = useState(false);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [formData, setFormData] = useState<Partial<Person>>({});
  // STAMM-4: Live-Status der E-Mail-Validierung.
  const [emailCheck, setEmailCheck] = useState<{ status: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'; email: string }>({ status: 'idle', email: '' });
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  // Doppelte Person hierher zusammenführen (T-0079).
  const [zusammenfuehrenOffen, setZusammenfuehrenOffen] = useState(false);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
  const [isAccessUpdating, setIsAccessUpdating] = useState(false);
  const [addressQuery, setAddressQuery] = useState<string>('');
  const [addressSuggestions, setAddressSuggestions] = useState<Array<Record<string, unknown>>>([]);
  // STAMM-8: Nach einer Auswahl soll der Autocomplete-Dropdown geschlossen
  // bleiben, bis der User wieder aktiv tippt. Sonst überschreibt der Hook
  // unsere Cleared-Liste sofort wieder mit Treffern zur gerade ausgewählten
  // (Voll-)Adresse.
  const [lastSelectedAddress, setLastSelectedAddress] = useState<string | null>(null);
  const { can } = usePermissions();
  // Laut Server statt nach Rolle: Person bearbeiten (Lehrerrolle, Standorte) und
  // Kontingente pflegen sind getrennte Permissions.
  const canEditPerson = can(P.personen.personen.person_bearbeiten);
  // T-0177: Archivieren und endgültig Löschen im Menü „…“ des Seitenkopfs — vorher
  // nur im Dialog „Person bearbeiten“, den keine Stelle mehr öffnete.
  const darfArchivieren = can(P.personen.personen.person_archivieren);
  const darfLoeschen = can(P.personen.personen.person_loeschen);
  const [archiviert, setArchiviert] = useState<boolean | null>(null);
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const canEditContingents = can(P.anmeldung.eintrag_bearbeiten);
  const [isContDialogOpen, setIsContDialogOpen] = useState(false);
  const [editingCont, setEditingCont] = useState<Contingent | null>(null);
  const [contForm, setContForm] = useState<{ name: string; totalUnits: string; remainingUnits: string; description: string }>({ name: '', totalUnits: '', remainingUnits: '', description: '' });
  const [isSubFormOpen, setIsSubFormOpen] = useState(false);
  const [editingSubIndex, setEditingSubIndex] = useState<number | null>(null);
  const [subprofiles, setSubprofiles] = useState<Array<Record<string, unknown>>>([]);
  // Lehrer-spezifische States
  const [teacherEditing, setTeacherEditing] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState<Array<{ id: number; name: string }>>([]);
  // T-0189: Vor der Lehrerrolle die Fächer wählen — ohne Fach gibt es sie nicht.
  const [lehrerFaecherOffen, setLehrerFaecherOffen] = useState(false);
  const [lehrerFaecher, setLehrerFaecher] = useState<number[]>([]);
  const [lehrerKuerzel, setLehrerKuerzel] = useState('');
  const [teacherForm, setTeacherForm] = useState<{ shortCode: string; taxNumber: string; bankAccountHolder: string; bankIban: string; subjectIds: number[]; portraitUrl: string; portraitAlt: string; publicBio: string }>({ shortCode: '', taxNumber: '', bankAccountHolder: '', bankIban: '', subjectIds: [], portraitUrl: '', portraitAlt: '', publicBio: '' });
  const [teachingArts, setTeachingArts] = useState<Array<{ id: number; name: string }>>([]);
  const [teacherArtRates, setTeacherArtRates] = useState<Record<number, string>>({});
  // Lehrer: Freigabezeiten (pro Standort)
  const [availabilityStandortId, setAvailabilityStandortId] = useState<number | null>(null);
  const [availabilitySlots, setAvailabilitySlots] = useState<TeacherAvailabilitySlot[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityEditing, setAvailabilityEditing] = useState(false);
  // Standort-Verwaltung
  const { standorte, selectedStandortId } = useStandort();
  const [personStandorteIds, setPersonStandorteIds] = useState<number[]>([]);
  const [abgeleiteteStandorteIds, setAbgeleiteteStandorteIds] = useState<number[]>([]);
  const [isEditingStandorte, setIsEditingStandorte] = useState(false);
  // Kündigung
  const [kuendigungWizardOpen, setKuendigungWizardOpen] = useState(false);
  // Zählt hoch, wenn sich Verträge ändern (Kündigung), damit die Kacheln neu laden.
  const [vertraegeStand, setVertraegeStand] = useState(0);
  // Newsletter-Einwilligung des Kontos (T-0154); null, solange sie lädt.
  const [werbemails, setWerbemails] = useState<boolean | null>(null);
  // Nachricht aus einem Kontaktweg heraus (T-0153): offen mit diesem Kanal.
  const [nachrichtKanal, setNachrichtKanal] = useState<'email' | 'sms' | null>(null);
  const anrufen = useAnrufen();
  const [kuendigungTeilnahmeId, setKuendigungTeilnahmeId] = useState<number>(0);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'general');
  const formCommunicationChannels = usePersonCommunicationChannels(
    (formData as Record<string, unknown>)?.communicationChannels ?? (formData as Record<string, unknown>)?.communications
  );
  useEffect(() => {
    const fetchPersonData = async () => {
      setIsLoading(true);
      setDetailError(null);
      try {
        const [types, p] = await Promise.all([
          personTypesApi.list(),
          api.get<Person>(`/api/personen/${id}`),
        ]);
        setPersonTypes(types.filter(t => t.aktiv));
        setPerson(p);
        // STAMM-2: Name in Vorname/Nachname aufteilen — falls Backend keine
        // separaten Felder liefert, fällt der Split heuristisch auf person.name zurück.
        const splitParts = String(p.name || '').trim().split(/\s+/).filter(Boolean);
        const initialFirstName = (p as Record<string, unknown>).firstName as string
          ?? (p as Record<string, unknown>).first_name as string
          ?? (splitParts.length > 1 ? splitParts.slice(0, -1).join(' ') : (splitParts[0] ?? ''));
        const initialLastName = (p as Record<string, unknown>).lastName as string
          ?? (p as Record<string, unknown>).last_name as string
          ?? (splitParts.length > 1 ? (splitParts[splitParts.length - 1] ?? '') : '');
        setFormData({
          ...p,
          firstName: initialFirstName,
          lastName: initialLastName,
          isCompany: Boolean((p as Record<string, unknown>).isCompany ?? false),
          companyName: ((p as Record<string, unknown>).companyName as string | null) ?? '',
          communicationChannels: normalizeCommunicationChannels(
            (p as Record<string, unknown>).communicationChannels ?? p.communications
          ),
          structuredAddress: toStructuredAddress(p.structuredAddress),
        });
        setAddressQuery(p.address || '');
        // Lehrer-Daten initialisieren
        const t = toTeacherPayload((p as Record<string, unknown>).teacher);
        setTeacherForm({
          shortCode: String(t.shortCode || ''),
          taxNumber: String(t.taxNumber || ''),
          bankAccountHolder: String(t.bank?.accountHolder || ''),
          bankIban: String(t.bank?.iban || ''),
          subjectIds: Array.isArray(t.subjects) ? t.subjects.map(Number) : [],
          portraitUrl: String(t.portraitUrl || ''),
          portraitAlt: String(t.portraitAlt || ''),
          publicBio: String(t.publicBio || ''),
        });
        const artRatesArr = Array.isArray(t.artRates) ? t.artRates : [];
        const artMap: Record<number, string> = {};
        for (const r of artRatesArr) {
          const aid = Number(r.unterrichtsform_art_id ?? r.art_id ?? 0);
          if (aid > 0) artMap[aid] = String(r.rate ?? '');
        }
        setTeacherArtRates(artMap);
        // Subprofile laden
        try {
          const list = await api.get<Array<Record<string, unknown>>>(`/api/personen/${id}/subprofiles`);
          const normalized = Array.isArray(list)
            ? [...list].sort((a, b) => Number(a?.index || 0) - Number(b?.index || 0))
            : [];
          setSubprofiles(normalized);
        } catch {
          setSubprofiles([]);
        }
        // Fächer laden
        try {
          const qs = selectedStandortId ? `?standort_id=${encodeURIComponent(String(selectedStandortId))}` : '';
          const faecher = await api.get<Array<Record<string, unknown>>>(`/api/faecher${qs}`);
          const opts = (faecher || []).map((f) => ({ id: Number(f.id), name: String(f.name) }));
          setSubjectOptions(opts);
        } catch {
          setSubjectOptions([]);
        }
        // Unterrichtsformen laden (+ Arten)
        try {
          const [forms, types] = await Promise.all([
            api.get<Array<Record<string, unknown>>>('/api/unterrichtsformen'),
            api.get<Array<Record<string, unknown>>>('/api/unterrichtsform-arten'),
          ]);
          const typeMap = new Map<number, { id: number; name: string }>();
          (types || []).forEach((t) => typeMap.set(Number(t.id), { id: Number(t.id), name: String(t.name) }));
          setTeachingArts((types || []).map((t) => ({ id: Number(t.id), name: String(t.name) })));
          void forms;
        } catch {
          setTeachingArts([]);
        }
        // Backend: Folge-Daten noch nicht implementiert → zunächst leer lassen
        try {
          const hist = await api.get<HistoryEntry[]>(`/api/personen/${id}/history`);
          setHistory(asArray<HistoryEntry>(hist));
        } catch {
          setHistory([]);
        }
        try {
          const conts = await api.get<Contingent[]>(`/api/personen/${id}/contingents`);
          setContingents(asArray<Contingent>(conts));
        } catch {
          setContingents([]);
        }
        try {
          const rows = await api.get<CustomerContract[]>(`/api/personen/${id}/contracts`);
          setContracts(asArray<CustomerContract>(rows));
        } catch {
          setContracts([]);
        }
        // Standorte laden
        try {
          const standorteIds = await api.get<number[]>(`/api/personen/${id}/standorte`);
          setPersonStandorteIds(standorteIds || []);
        } catch {
          setPersonStandorteIds([]);
        }
        try {
          const abgeleitet = await api.get<number[]>(`/api/personen/${id}/standorte?abgeleitete=1`);
          setAbgeleiteteStandorteIds(asArray<number>(abgeleitet));
        } catch {
          setAbgeleiteteStandorteIds([]);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Personendaten:', error);
        setPerson(null);
        setDetailError('Personendaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPersonData();
  }, [id, selectedStandortId]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = useCallback((nextTab: string) => {
    setActiveTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Default-Standort für Freigabezeiten setzen (erster zugeordneter)
  useEffect(() => {
    if (availabilityStandortId !== null) return;
    if (personStandorteIds.length === 0) return;
    setAvailabilityStandortId(personStandorteIds[0]);
  }, [personStandorteIds, availabilityStandortId]);

  // Freigabezeiten laden, sobald Person+Standort vorhanden
  useEffect(() => {
    const load = async () => {
      if (!person) return;
      if (!(person.type === 'teacher' || person.type === 'both')) return;
      if (!availabilityStandortId) return;
      setAvailabilityLoading(true);
      try {
        const wpId = person.wpUserId || person.id;
        const slots = await personenApi.getTeacherAvailability(wpId, availabilityStandortId);
        setAvailabilitySlots(slots || []);
      } catch {
        setAvailabilitySlots([]);
      } finally {
        setAvailabilityLoading(false);
      }
    };
    load();
  }, [person, availabilityStandortId]);

  const daysOfWeek: Array<{ id: number; label: string }> = [
    { id: 1, label: 'Montag' },
    { id: 2, label: 'Dienstag' },
    { id: 3, label: 'Mittwoch' },
    { id: 4, label: 'Donnerstag' },
    { id: 5, label: 'Freitag' },
    { id: 6, label: 'Samstag' },
    { id: 7, label: 'Sonntag' },
  ];

  const setSlotTime = (idx: number, field: 'time_start' | 'time_end', valueHHMM: string) => {
    const v = (valueHHMM || '').trim();
    const normalized = v && v.length === 5 ? `${v}:00` : '';
    setAvailabilitySlots(prev => prev.map((s, i) => (i === idx ? { ...s, [field]: normalized } : s)));
  };

  const addSlotForDay = (dayOfWeek: number) => {
    setAvailabilitySlots(prev => ([
      ...prev,
      { dayOfWeek, time_start: '09:00:00', time_end: '10:00:00' },
    ]));
  };

  const removeSlot = (idx: number) => {
    setAvailabilitySlots(prev => prev.filter((_, i) => i !== idx));
  };
  // Get profile type description
  const getProfileTypeDescription = () => {
    if (person?.parentId) {
      const pt = personTypes.find(t => t.code === 'student');
      return `Zugeordnete Person (${pt?.name || 'Lernende'})`;
    }
    if (person?.type === 'none') return 'Noch ohne Rolle';
    const match = personTypes.find(t => t.code === person?.type);
    return match?.name || person?.type || '';
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (name: string, checked: boolean | 'indeterminate') => {
    setFormData(prev => {
      const next: Partial<Person> = {
        ...prev,
        [name]: checked === true,
      };
      if (name === 'isCompany') {
        if (checked === true) {
          next.salutation = 'Firma';
        } else {
          next.companyName = '';
          if (prev.salutation === 'Firma') {
            next.salutation = 'Herr';
          }
        }
      }
      return next;
    });
  };

  const addCommunication = () => {
    setFormData(prev => ({
      ...prev,
      communicationChannels: [...normalizeCommunicationChannels((prev as Record<string, unknown>).communicationChannels), { type: 'mobil', value: '', officeContactAllowed: true, isActive: true }],
    }));
  };
  const updateCommunication = (index: number, field: 'type' | 'value', value: string) => {
    setFormData(prev => {
      const list = [...normalizeCommunicationChannels((prev as Record<string, unknown>).communicationChannels)];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, communicationChannels: list };
    });
  };
  const removeCommunication = (index: number) => {
    setFormData(prev => {
      const list = [...normalizeCommunicationChannels((prev as Record<string, unknown>).communicationChannels)];
      list.splice(index, 1);
      return { ...prev, communicationChannels: list };
    });
  };
  const setCommunicationOfficeAllowed = (index: number, allowed: boolean) => {
    setFormData(prev => {
      const list = [...normalizeCommunicationChannels((prev as Record<string, unknown>).communicationChannels)];
      list[index] = { ...list[index], officeContactAllowed: allowed };
      return { ...prev, communicationChannels: list };
    });
  };

  // Altersanzeige berechnen (Jahre/Monate/Tage)
  const getAgeText = (dateString?: string) => {
    if (!dateString) return '';
    const birth = new Date(dateString);
    if (isNaN(birth.getTime())) return '';
    const now = new Date();
    let years = now.getFullYear() - birth.getFullYear();
    let months = now.getMonth() - birth.getMonth();
    let days = now.getDate() - birth.getDate();
    if (days < 0) {
      // Vormonat
      const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += prevMonth.getDate();
      months -= 1;
    }
    if (months < 0) {
      months += 12;
      years -= 1;
    }
    return `${years} Jahre ${months} Monate ${days} Tage`;
  };

  const { suggestions: addressSuggestionsHook } = useGeoapifyAddressAutocomplete({
    query: addressQuery,
    enabled: isEditing,
  });

  useEffect(() => {
    // STAMM-8: Nach Auswahl Treffer-Liste unterdrücken, bis der User wieder tippt.
    if (lastSelectedAddress !== null && addressQuery === lastSelectedAddress) {
      setAddressSuggestions([]);
      return;
    }
    setAddressSuggestions(addressSuggestionsHook as Array<Record<string, unknown>>);
  }, [addressSuggestionsHook, addressQuery, lastSelectedAddress]);
  // STAMM-4: debouncter Live-Check der E-Mail-Adresse gegen das Backend.
  useEffect(() => {
    if (!isEditing || !person) return;
    const email = String(formData.email || '').trim();
    const personId = person.wpUserId || person.id;
    if (email === '') {
      setEmailCheck({ status: 'idle', email });
      return;
    }
    if (email === person.email) {
      setEmailCheck({ status: 'available', email });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setEmailCheck({ status: 'invalid', email });
      return;
    }
    setEmailCheck({ status: 'checking', email });
    const timer = setTimeout(async () => {
      try {
        const res = await api.get<{ available: boolean }>(`/api/personen/${personId}/check-email?email=${encodeURIComponent(email)}`);
        setEmailCheck({ status: res.available ? 'available' : 'taken', email });
      } catch {
        // Konservativ: bei Lookup-Fehler nicht blockieren, aber auch nicht freigeben.
        setEmailCheck({ status: 'idle', email });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.email, isEditing, person]);

  const handleLockAccess = async () => {
    if (!person) return;
    setIsAccessUpdating(true);
    try {
      const res = await personenAccessApi.lockAccess(person.wpUserId || person.id);
      setPerson(res.person as Person);
      setFormData(prev => ({ ...prev, ...(res.person as Person) }));
      setLockConfirmOpen(false);
      toast.success('Zugang gesperrt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sperren fehlgeschlagen');
    } finally {
      setIsAccessUpdating(false);
    }
  };

  const handleUnlockAccess = async () => {
    if (!person) return;
    setIsAccessUpdating(true);
    try {
      const res = await personenAccessApi.unlockAccess(person.wpUserId || person.id);
      setPerson(res.person as Person);
      setFormData(prev => ({ ...prev, ...(res.person as Person) }));
      setUnlockConfirmOpen(false);
      toast.success('Zugang freigeschaltet');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Freischalten fehlgeschlagen');
    } finally {
      setIsAccessUpdating(false);
    }
  };

  const istArchiviert = archiviert ?? person?.status === 'inactive';

  const archivierenUmschalten = async () => {
    if (!person) return;
    const ziel = !istArchiviert;
    try {
      await api.post(`/api/personen/${person.wpUserId || person.id}/archivieren`, { archiviert: ziel });
      setArchiviert(ziel);
      setPerson((alt) => (alt ? { ...alt, status: ziel ? 'inactive' : 'active' } : alt));
      toast.success(ziel ? 'Person archiviert' : 'Person zurückgeholt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : ziel ? 'Archivieren fehlgeschlagen' : 'Zurückholen fehlgeschlagen');
    }
  };

  const handlePromoteToTeacher = () => {
    setLehrerFaecher([]);
    setLehrerKuerzel('');
    setLehrerFaecherOffen(true);
  };

  const lehrerRolleVergeben = async () => {
    if (!person) return;
    if (lehrerFaecher.length === 0) {
      toast.error(`Bitte mindestens 1 ${getSubjectLabels().singular} auswählen`);
      return;
    }
    if (lehrerKuerzel.trim() === '') {
      toast.error('Bitte ein Kürzel angeben (z. B. „HaMü“)');
      return;
    }
    setIsLoading(true);
    try {
      const res = await personenAccessApi.promoteTeacher(
        person.wpUserId || person.id,
        lehrerFaecher,
        lehrerKuerzel.trim(),
      );
      setPerson(res.person as Person);
      setFormData(prev => ({ ...prev, ...(res.person as Person) }));
      setLehrerFaecherOffen(false);
      toast.success('Lehrer-Rolle hinzugefügt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Rolle konnte nicht gesetzt werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!person) return;
    // STAMM-4: bei Konflikt/ungültig nicht speichern.
    if (emailCheck.status === 'taken' || emailCheck.status === 'invalid') {
      toast.error(emailCheck.status === 'taken'
        ? 'E-Mail bereits vergeben — bitte eine andere Adresse wählen.'
        : 'E-Mail-Format ungültig.');
      return;
    }
    setIsLoading(true);
    try {
      // STAMM-2: Vor-/Nachname direkt aus den separaten Form-Feldern,
      // kein Heuristik-Split mehr.
      const firstName = String(formData.firstName || '').trim();
      const lastName = String(formData.lastName || '').trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const isCompany = Boolean(formData.isCompany);
      const companyName = String(formData.companyName || '').trim();

      const payload: Record<string, unknown> = {
        // STAMM-4: explizit gesetzte E-Mail überschreibt die aus den Channels
        // abgeleitete — das ist die vom User in der Maske eingegebene Adresse.
        email: String(formData.email || '').trim() || derivePrimaryEmailFromChannels(formCommunicationChannels),
        // Display-Name: bei Firma der Firmenname, sonst Vor- + Nachname.
        display_name: isCompany && companyName !== '' ? companyName : fullName,
        salutation: formData.salutation,
        newsletter: formData.newsletter === true,
        birthDate: formData.birthDate,
        communicationChannels: formCommunicationChannels,
        isCompany,
        companyName: isCompany ? companyName : '',
      };
      if (firstName !== '' || lastName !== '') {
        payload.first_name = firstName;
        payload.last_name = lastName;
      }
      if (formData.structuredAddress) {
        const structured = toStructuredAddress(formData.structuredAddress);
        const zip = String(structured.zip || structured.postalCode || '').trim();
        payload.structuredAddress = {
          ...structured,
          zip,
          postalCode: zip,
        };
        // Den formatierten Anzeige-String mitschicken, damit `address` im Backend
        // nicht leer bleibt (Listen/E-Mails lesen `address`, die Detail-Anzeige
        // fällt sonst auf die strukturierte Adresse zurück).
        const line1 = `${structured.street || ''} ${structured.houseNumber || ''}`.trim();
        const line2 = `${zip} ${structured.city || ''}`.trim();
        const formatted = String(structured.formatted || '').trim()
          || [line1, line2].filter(Boolean).join(', ');
        if (formatted) {
          payload.address = formatted;
        }
      } else if (formData.address) {
        payload.address = formData.address;
      }
      const updated = await api.patch<Person>(`/api/personen/${person.wpUserId || person.id}`, payload);
      setPerson(updated);
      const updatedParts = String(updated.name || '').trim().split(/\s+/).filter(Boolean);
      const updatedFirst = (updated as Record<string, unknown>).firstName as string
        ?? (updated as Record<string, unknown>).first_name as string
        ?? (updatedParts.length > 1 ? updatedParts.slice(0, -1).join(' ') : (updatedParts[0] ?? ''));
      const updatedLast = (updated as Record<string, unknown>).lastName as string
        ?? (updated as Record<string, unknown>).last_name as string
        ?? (updatedParts.length > 1 ? (updatedParts[updatedParts.length - 1] ?? '') : '');
      setFormData({
        ...updated,
        firstName: updatedFirst,
        lastName: updatedLast,
        communicationChannels: normalizeCommunicationChannels(
          ((updated as Record<string, unknown>).communicationChannels ?? (updated as Person).communications)
        ),
        structuredAddress: toStructuredAddress((updated as Person).structuredAddress),
      });
      setAddressQuery(updated.address || '');
      setIsEditing(false);
      toast.success('Person gespeichert');
    } catch (error) {
      console.error('Fehler beim Speichern der Personendaten:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  // Subprofile bearbeiten
  const openAddSubprofile = () => {
    setEditingSubIndex(null);
    setIsSubFormOpen(true);
  };
  const openEditSubprofile = (index: number) => {
    setEditingSubIndex(index);
    setIsSubFormOpen(true);
  };
  const saveSubprofile = async (data: Record<string, unknown>) => {
    if (!person) return;
    try {
      if (editingSubIndex === null) {
        const created = await api.post<Record<string, unknown>>(`/api/personen/${person.wpUserId || person.id}/subprofiles`, data);
        setSubprofiles(prev => [...prev, created]);
        toast.success('Zugeordnete Person hinzugefügt');
      } else {
        const sp = subprofiles[editingSubIndex];
        const updated = await api.put<Record<string, unknown>>(`/api/personen/${person.wpUserId || person.id}/subprofiles/${sp.id}`, data);
        setSubprofiles(prev => prev.map((it, idx) => (idx === editingSubIndex ? updated : it)));
        toast.success('Zugeordnete Person aktualisiert');
      }
    } catch (e) {
      toast.error('Zugeordnete Person konnte nicht gespeichert werden');
    } finally {
      setIsSubFormOpen(false);
      setEditingSubIndex(null);
    }
  };
  const deleteSubprofile = (index: number) => {
    if (!person) return;
    const sp = subprofiles[index];
    if (Number(sp?.index || 0) === 1) {
      toast.error('Das Hauptprofil kann nicht gelöscht werden');
      return;
    }
    setZuLoeschen({ art: 'profil', index, name: String(sp?.name || '') });
  };
  const subprofilLoeschen = async (index: number, grund: string) => {
    if (!person) return;
    const sp = subprofiles[index];
    await api.delete(`/api/personen/${person.wpUserId || person.id}/subprofiles/${sp.id}`, grundHeader(grund));
    setSubprofiles(prev => prev.filter((_, idx) => idx !== index));
    setZuLoeschen(null);
    toast.success('Zugeordnete Person gelöscht');
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'locked':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'paid':
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'overdue':
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'locked':
        return 'Gesperrt';
      case 'inactive':
        return 'Inaktiv';
      case 'pending':
        return 'Ausstehend';
      case 'paid':
        return 'Bezahlt';
      case 'overdue':
        return 'Überfällig';
      case 'completed':
        return 'Erledigt';
      case 'failed':
        return 'Fehlgeschlagen';
      case 'high':
        return 'Hoch';
      case 'medium':
        return 'Mittel';
      case 'low':
        return 'Niedrig';
      default:
        return status;
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'student':
        return <GraduationCap className="h-5 w-5 text-indigo-500" />;
      case 'teacher':
        return <Briefcase className="h-5 w-5 text-emerald-500" />;
      case 'both':
        return <div className="flex">
            <GraduationCap className="h-5 w-5 text-indigo-500" />
            <Briefcase className="ml-1 h-5 w-5 text-emerald-500" />
          </div>;
      default:
        return <User className="h-5 w-5" />;
    }
  };
  const getHistoryIcon = (type: string) => {
    switch (type) {
      case 'data_change':
        return <FileEdit className="h-5 w-5 text-blue-500" />;
      case 'course_change':
        return <BookOpen className="h-5 w-5 text-purple-500" />;
      case 'status_change':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'payment':
        return <DollarSign className="h-5 w-5 text-green-500" />;
      case 'note':
        return <MessageCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Info className="h-5 w-5 text-gray-500" />;
    }
  };
  // Get package details for a contingent
  // Calculate total available units
  const getTotalAvailableUnits = () => {
    return contingents.reduce((total, c: Contingent) => total + (c.remainingUnits || 0), 0);
  };
  // Calculate total used units
  const getTotalUsedUnits = () => {
    return contingents.reduce((total, c: Contingent) => total + ((c.totalUnits || 0) - (c.remainingUnits || 0)), 0);
  };
  // Calculate total purchased units
  const getTotalPurchasedUnits = () => {
    return contingents.reduce((total, c: Contingent) => total + (c.totalUnits || 0), 0);
  };
  const openContractPdf = async (orderId: number) => {
    try {
      const result = await api.get<{ url: string }>(`/api/orders/${orderId}/download-url`);
      if (result?.url) {
        // Kann eine data:-URL sein — die öffnet der Browser nicht direkt (T-0265).
        openTeacherInvoiceDocument(result.url);
      } else {
        toast.error('PDF-Link nicht verfügbar');
      }
    } catch {
      toast.error('PDF-Link konnte nicht geladen werden');
    }
  };
  // Admin-Contingents CRUD
  const openNewContingent = () => {
    setEditingCont(null);
    setContForm({ name: '', totalUnits: '', remainingUnits: '', description: '' });
    setIsContDialogOpen(true);
  };
  const openEditContingent = (c: Contingent) => {
    setEditingCont(c);
    setContForm({ name: c.name || '', totalUnits: String(c.totalUnits ?? ''), remainingUnits: String(c.remainingUnits ?? ''), description: c.description || '' });
    setIsContDialogOpen(true);
  };
  const saveContingent = async () => {
    if (!person) return;
    const payload: Record<string, unknown> = {
      name: contForm.name.trim(),
      totalUnits: parseInt(contForm.totalUnits || '0', 10),
      remainingUnits: contForm.remainingUnits !== '' ? parseInt(contForm.remainingUnits, 10) : undefined,
      description: contForm.description || undefined,
    };
    const totalUnits = Number(payload.totalUnits);
    const remainingUnits = payload.remainingUnits === undefined
      ? totalUnits
      : Number(payload.remainingUnits);
    if (
      !payload.name
      || totalUnits <= 0
      || remainingUnits < 0
      || remainingUnits > totalUnits
    ) {
      toast.error('Bitte Namen und gültige Einheiten angeben');
      return;
    }
    try {
      if (editingCont) {
        const updated = await api.put<Contingent>(`/api/personen/${person.wpUserId || person.id}/contingents/${editingCont.id}`, payload);
        setContingents(prev => prev.map(c => (String(c.id) === String(updated.id) ? updated : c)));
        toast.success('Kontingent aktualisiert');
      } else {
        const created = await api.post<Contingent>(`/api/personen/${person.wpUserId || person.id}/contingents`, payload);
        setContingents(prev => [created, ...prev]);
        toast.success('Kontingent angelegt');
      }
      setIsContDialogOpen(false);
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };
  const deleteContingent = (c: Contingent) => {
    if (!person) return;
    setZuLoeschen({ art: 'kontingent', kontingent: c });
  };
  const kontingentLoeschen = async (c: Contingent, grund: string) => {
    if (!person) return;
    await api.delete(`/api/personen/${person.wpUserId || person.id}/contingents/${c.id}`, grundHeader(grund));
    setContingents(prev => prev.filter(x => String(x.id) !== String(c.id)));
    setZuLoeschen(null);
    toast.success('Kontingent gelöscht');
  };
  const handleTeacherCancelEdit = () => {
    if (!person) return;
    const t = toTeacherPayload((person as Record<string, unknown>).teacher);
    setTeacherForm({
      shortCode: String(t.shortCode || ''),
      taxNumber: String(t.taxNumber || ''),
      bankAccountHolder: String(t.bank?.accountHolder || ''),
      bankIban: String(t.bank?.iban || ''),
      subjectIds: Array.isArray(t.subjects) ? t.subjects.map(Number) : [],
      portraitUrl: String(t.portraitUrl || ''),
      portraitAlt: String(t.portraitAlt || ''),
      publicBio: String(t.publicBio || ''),
    });
    setTeacherEditing(false);
  };
  // Lehrer-Rolle aktivieren/deaktivieren — ohne den Bearbeiten-Modus (T-0043).
  const handleTeacherRoleActive = async (aktiv: boolean) => {
    if (!person) return;
    try {
      const updated = await api.put<Person>(`/api/personen/${person.wpUserId || person.id}/teacher`, {
        roleActive: aktiv,
      });
      setPerson(updated);
      toast.success(aktiv ? 'Lehrer-Rolle aktiviert' : 'Lehrer-Rolle deaktiviert');
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : 'Die Lehrer-Rolle konnte nicht geändert werden');
    }
  };
  const handleTeacherSave = async () => {
    if (!person) return;
    if (teacherForm.subjectIds.length === 0) {
      toast.error(`Bitte mindestens 1 ${getSubjectLabels().singular} auswählen`);
      return;
    }
    if (teacherForm.shortCode.trim() === '') {
      toast.error('Bitte ein Kürzel angeben (z. B. „HaMü“)');
      return;
    }
    try {
      const artRatesPayload = Object.entries(teacherArtRates)
        .filter(([, v]) => String(v).trim() !== '')
        .map(([k, v]) => ({ unterrichtsform_art_id: Number(k), rate: parseFloat(String(v)) }));
      const updated = await api.put<Person>(`/api/personen/${person.wpUserId || person.id}/teacher`, {
        shortCode: teacherForm.shortCode || undefined,
        taxNumber: teacherForm.taxNumber || undefined,
        bank: { accountHolder: teacherForm.bankAccountHolder || '', iban: teacherForm.bankIban || '' },
        subjects: teacherForm.subjectIds,
        artRates: artRatesPayload,
        portraitUrl: teacherForm.portraitUrl || '',
        portraitAlt: teacherForm.portraitAlt || '',
        publicBio: teacherForm.publicBio || '',
      });
      setPerson(updated);
      const tNew = toTeacherPayload((updated as Record<string, unknown>).teacher);
      setTeacherForm({
        shortCode: String(tNew.shortCode || ''),
        taxNumber: String(tNew.taxNumber || ''),
        bankAccountHolder: String(tNew.bank?.accountHolder || ''),
        bankIban: String(tNew.bank?.iban || ''),
        subjectIds: Array.isArray(tNew.subjects) ? tNew.subjects.map(Number) : [],
        portraitUrl: String(tNew.portraitUrl || ''),
        portraitAlt: String(tNew.portraitAlt || ''),
        publicBio: String(tNew.publicBio || ''),
      });
      const newArtRates = Array.isArray(tNew.artRates) ? tNew.artRates : [];
      const nextArt: Record<number, string> = {};
      for (const r of newArtRates) {
        const aid = Number(r.unterrichtsform_art_id ?? r.art_id ?? 0);
        if (aid > 0) nextArt[aid] = String(r.rate ?? '');
      }
      setTeacherArtRates(nextArt);
      setTeacherEditing(false);
      toast.success('Lehrer-Daten gespeichert');
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : 'Speichern fehlgeschlagen');
    }
  };
  const handleAvailabilityReload = async () => {
    if (!person || !availabilityStandortId) return;
    try {
      setAvailabilityLoading(true);
      const wpId = person.wpUserId || person.id;
      const slots = await personenApi.getTeacherAvailability(wpId, availabilityStandortId);
      setAvailabilitySlots(slots || []);
      setAvailabilityEditing(false);
      toast.success('Änderungen verworfen');
    } catch {
      toast.error('Konnte Freigabezeiten nicht neu laden');
    } finally {
      setAvailabilityLoading(false);
    }
  };
  const handleAvailabilitySave = async () => {
    if (!person || !availabilityStandortId) return;
    try {
      setAvailabilityLoading(true);
      const wpId = person.wpUserId || person.id;
      const saved = await personenApi.setTeacherAvailability(wpId, availabilityStandortId, availabilitySlots);
      setAvailabilitySlots(saved || []);
      setAvailabilityEditing(false);
      toast.success('Freigabezeiten gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setAvailabilityLoading(false);
    }
  };
  const handleAddressQueryChange = (value: string) => {
    // STAMM-8: Sobald der User tippt, wird der "just selected"-Schutz aufgehoben,
    // damit neue Vorschläge wieder erscheinen.
    if (value !== lastSelectedAddress) {
      setLastSelectedAddress(null);
    }
    setAddressQuery(value);
    setFormData(prev => ({
      ...prev,
      address: value,
      structuredAddress: {
        ...toStructuredAddress(prev.structuredAddress),
        formatted: value,
      },
    }));
  };
  const handleAddressSuggestionSelect = (suggestion: Record<string, unknown>) => {
    const structured = toStructuredAddress((suggestion as Record<string, unknown>)?.structured);
    const formatted = String(structured.formatted || '').trim();
    const zip = String(structured.zip || structured.postalCode || '').trim();
    // STAMM-8: Hausnummer mit ins Straße-Feld übernehmen — sonst landet sie nur
    // im (unsichtbaren) houseNumber-Feld und verschwindet aus der Anzeige.
    const baseStreet = String(structured.street || '').trim();
    const houseNumber = String((structured as Record<string, unknown>).houseNumber || '').trim();
    const combinedStreet = [baseStreet, houseNumber].filter(Boolean).join(' ');
    setFormData(prev => ({
      ...prev,
      address: formatted,
      structuredAddress: {
        ...structured,
        street: combinedStreet || baseStreet,
        // STAMM-8: Hausnummer ist jetzt Teil von street → nicht zusätzlich tragen,
        // sonst verdoppelt handleStructuredAddressField sie beim Re-Formatieren.
        houseNumber: '',
        zip,
        postalCode: zip,
      },
    }));
    setAddressQuery(formatted);
    setAddressSuggestions([]);
    // STAMM-8: ausgewählte Adresse merken, damit der Treffer-Dropdown nicht
    // sofort wieder durch denselben Query "neu öffnet".
    setLastSelectedAddress(formatted);
  };
  const handleStructuredAddressField = (field: 'street' | 'zip' | 'city', value: string) => {
    setFormData((prev) => {
      const current = toStructuredAddress(prev.structuredAddress);
      const next: Record<string, unknown> = { ...current, [field]: value };
      if (field === 'zip') {
        next.postalCode = value;
      }
      const line1 = `${next.street || ''} ${next.houseNumber || ''}`.trim();
      const line2 = `${next.zip || next.postalCode || ''} ${next.city || ''}`.trim();
      next.formatted = [line1, line2].filter(Boolean).join(', ');
      return { ...prev, structuredAddress: next, address: next.formatted || prev.address };
    });
  };
  const handleToggleStandort = (standortId: number, enabled: boolean) => {
    setPersonStandorteIds((prev) => (
      enabled ? [...prev, standortId] : prev.filter((id) => id !== standortId)
    ));
  };
  const handleSaveStandorte = async () => {
    if (!person) return;
    try {
      await api.put(`/api/personen/${person.wpUserId || person.id}/standorte`, {
        standort_ids: personStandorteIds,
      });
      toast.success('Standorte aktualisiert');
      setIsEditingStandorte(false);
    } catch (e) {
      toast.error('Fehler beim Speichern der Standorte');
    }
  };
  const handleCancelStandorteEdit = () => {
    setIsEditingStandorte(false);
    if (person) {
      api.get<number[]>(`/api/personen/${person.wpUserId || person.id}/standorte`)
        .then(setPersonStandorteIds)
        .catch(() => setPersonStandorteIds([]));
    }
  };
  const showTeacherTab = person?.type === 'teacher' || person?.type === 'both';
  // Lernende lassen sich auch an einem Datensatz ohne Rolle anlegen — der wird
  // erst mit der Registrierung oder der ersten Anmeldung zum Kunden (T-0297).
  const showAssociatedPersonsTab = person?.type === 'customer' || person?.type === 'both' || person?.type === 'none' || subprofiles.length > 0;
  const hasWpUser = person?.hasWpUser !== false;
  const onboardingCompleted = (() => {
    const value = (person as Record<string, unknown> | null)?.onboardingCompleted;
    return value === true || value === 1 || value === '1' || value === 'true';
  })();
  const accessLocked = person?.status === 'locked'
    || person?.accessLocked === true
    || (person as Record<string, unknown> | null)?.accessLocked === 1
    || (person as Record<string, unknown> | null)?.accessLocked === '1';
  const sendOnboarding = useCallback(async () => {
    if (!person) return;
    try {
      setIsLoading(true);
      await api.post(`/api/personen/${person.wpUserId || person.id}/onboarding/resend`, {});
      toast.success('Registrierungsmail gesendet');
    } catch {
      toast.error('Versand fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  }, [person]);
  const personIdFuerWerbung = person?.id;
  useEffect(() => {
    if (!personIdFuerWerbung) return;
    let abgebrochen = false;
    api
      .get<{ werbemails?: boolean }>(`/api/personen/${personIdFuerWerbung}/werbemails`)
      .then((res) => {
        if (!abgebrochen) setWerbemails(res?.werbemails === true);
      })
      .catch(() => {
        if (!abgebrochen) setWerbemails(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [personIdFuerWerbung]);
  const standortFuerKontakt = selectedStandortId || personStandorteIds[0] || 0;
  const anrufenAusDatensatz = async (nummer: string) => {
    if (!person) return;
    const vermerk = await anrufen(nummer, standortFuerKontakt);
    if (!vermerk) return;
    try {
      await api.post(`/api/personen/${person.id}/history`, { type: 'call', description: vermerk });
      await historieNeuLaden();
    } catch {
      // Der Anruf selbst ist gestartet; ohne Vermerk geht es trotzdem weiter.
    }
  };
  const werbemailsSetzen = async (an: boolean) => {
    if (!person) return;
    const vorher = werbemails;
    setWerbemails(an);
    try {
      await api.put(`/api/personen/${person.id}/werbemails`, { werbemails: an });
      toast.success(an ? 'Werbemails aktiviert' : 'Werbemails deaktiviert');
    } catch {
      setWerbemails(vorher);
      toast.error('Einstellung konnte nicht gespeichert werden');
    }
  };
  useEffect(() => {
    if (!person) return;
    const allowedTabs = new Set<string>(['general', 'customer']);
    if (showTeacherTab) allowedTabs.add('teacher');
    if (!allowedTabs.has(activeTab)) {
      handleTabChange('general');
    }
  }, [person, showTeacherTab, activeTab, handleTabChange]);
  if (isLoading && !person) {
    return <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  if (detailError && !person) {
    return <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(peopleListPath)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zur Personenliste
        </Button>
        <div className="rounded-lg border bg-card p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <h2 className="mt-4 text-lg font-semibold">Fehler beim Laden</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {detailError}
          </p>
          <Button className="mt-4" onClick={() => globalThis.location.reload()}>
            Erneut versuchen
          </Button>
        </div>
      </div>;
  }
  if (!person) {
    return <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(peopleListPath)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zur Personenliste
        </Button>
        <div className="rounded-lg border bg-card p-8 text-center">
          <User className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">Person nicht gefunden</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Die gesuchte Person konnte nicht gefunden werden.
          </p>
        </div>
      </div>;
  }
  return <div className="mx-auto max-w-7xl space-y-6">
      <div className="text-sm text-muted-foreground">
        <button className="hover:underline" onClick={() => navigate(peopleListPath)}>Personen</button>
        <span className="mx-2">/</span>
        <span>{person.name}</span>
      </div>
      <div className="flex items-start justify-between">
        <Button variant="ghost" onClick={() => navigate(peopleListPath)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zur Personenliste
        </Button>
        <div className="flex flex-row items-center gap-2">
          {can(P.personen.personen.person_zusammenfuehren) ? (
            <Button variant="outline" onClick={() => setZusammenfuehrenOffen(true)}>
              Dublette zusammenführen
            </Button>
          ) : null}
          {!hasWpUser ? (
            // Schickt den Onboarding-Link; darüber legt die Person ihren Zugang an.
            // Vorher war der Knopf fest deaktiviert (T-0076).
            <Button
              onClick={sendOnboarding}
              disabled={isLoading || !person.email}
              title={person.email ? 'Registrierungslink per E-Mail senden' : 'Für einen Zugang fehlt die E-Mail-Adresse'}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Zugang erstellen
            </Button>
          ) : accessLocked ? (
            <Button variant="outline" onClick={() => setUnlockConfirmOpen(true)} disabled={isLoading || isAccessUpdating}>
              <LockOpen className="mr-2 h-4 w-4" />
              Zugang freischalten
            </Button>
          ) : !onboardingCompleted ? (
            <Button onClick={sendOnboarding} disabled={isLoading}>
              <Mail className="mr-2 h-4 w-4" />
              Registrierung abschließen
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={sendOnboarding} disabled={isLoading}>
                <Mail className="mr-2 h-4 w-4" />
                Zugangsdaten zurücksetzen
              </Button>
              <Button variant="outline" className="text-destructive" onClick={() => setLockConfirmOpen(true)} disabled={isLoading || isAccessUpdating}>
                <Lock className="mr-2 h-4 w-4" />
                Zugang sperren
              </Button>
            </>
          )}
          {darfArchivieren || darfLoeschen ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Weitere Aktionen">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {darfArchivieren ? (
                  <DropdownMenuItem onClick={() => void archivierenUmschalten()}>
                    {istArchiviert ? (
                      <><ArchiveRestore className="mr-2 h-4 w-4" /> Zurückholen</>
                    ) : (
                      <><Archive className="mr-2 h-4 w-4" /> Archivieren</>
                    )}
                  </DropdownMenuItem>
                ) : null}
                {darfArchivieren && darfLoeschen ? <DropdownMenuSeparator /> : null}
                {darfLoeschen ? (
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setLoeschenOffen(true)}>
                    <Trash2 className="mr-2 h-4 w-4" /> Endgültig löschen
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <LoeschenMitGrundDialog
        offen={loeschenOffen}
        titel="Person endgültig löschen"
        beschreibung={`${person.name} wird mit der ganzen Akte gelöscht. Soll die Person nur aus den aktiven Listen verschwinden, archiviere sie stattdessen.`}
        onAbbrechen={() => setLoeschenOffen(false)}
        onLoeschen={async (grund) => {
          await api.delete(`/api/personen/${person.wpUserId || person.id}`, grundHeader(grund));
          setLoeschenOffen(false);
          toast.success('Person gelöscht');
          navigate(peopleListPath);
        }}
      />

      <PersonDetailHeader
        person={person}
        profileTypeDescription={getProfileTypeDescription()}
        renderTypeIcon={getTypeIcon}
        statusText={getStatusText(person.status)}
        statusClassName={getStatusBadgeClass(person.status)}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <Dialog open={lehrerFaecherOffen} onOpenChange={setLehrerFaecherOffen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Lehrer-Rolle hinzufügen</DialogTitle>
              <DialogDescription>Welche {getSubjectLabels().plural} unterrichtet die Person? Mindestens 1 Eintrag ist Pflicht, das Kürzel auch.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <label htmlFor="lehrerKuerzel" className="text-sm font-medium">Kürzel</label>
              <Input
                id="lehrerKuerzel"
                value={lehrerKuerzel}
                onChange={(e) => setLehrerKuerzel(e.target.value)}
                placeholder="z. B. HaMü"
                maxLength={64}
              />
            </div>
            <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto">
              {subjectOptions.map((fach) => (
                <label key={fach.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={lehrerFaecher.includes(fach.id)}
                    onChange={(e) =>
                      setLehrerFaecher((alt) =>
                        e.target.checked ? [...alt, fach.id] : alt.filter((id) => id !== fach.id)
                      )
                    }
                  />
                  {fach.name}
                </label>
              ))}
              {subjectOptions.length === 0 ? (
                <p className="col-span-2 text-sm text-muted-foreground">Keine {getSubjectLabels().plural} angelegt.</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLehrerFaecherOffen(false)} disabled={isLoading}>
                Abbrechen
              </Button>
              <Button
                onClick={() => void lehrerRolleVergeben()}
                disabled={isLoading || lehrerFaecher.length === 0 || lehrerKuerzel.trim() === ''}
              >
                Rolle hinzufügen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PersonDetailTabs
          showTeacherTab={showTeacherTab}
          canAddTeacherRole={!showTeacherTab && canEditPerson}
          onAddTeacherRole={handlePromoteToTeacher}
        />

        <PersonOverviewTab
          person={person}
          isEditing={isEditing}
          isSaving={isLoading}
          formData={formData}
          personTypes={personTypes}
          getTypeIcon={getTypeIcon}
          getProfileTypeDescription={getProfileTypeDescription}
          getStatusBadgeClass={getStatusBadgeClass}
          getStatusText={getStatusText}
          getAgeText={getAgeText}
          handleSelectChange={handleSelectChange}
          handleInputChange={handleInputChange}
          handleCheckboxChange={handleCheckboxChange}
          addressQuery={addressQuery}
          onAddressQueryChange={handleAddressQueryChange}
          addressSuggestions={addressSuggestions}
          onAddressSuggestionSelect={handleAddressSuggestionSelect}
          onStructuredAddressFieldChange={handleStructuredAddressField}
          onCommunicationTypeChange={(index, value) => updateCommunication(index, 'type', value)}
          onCommunicationValueChange={(index, value) => updateCommunication(index, 'value', value)}
          onCommunicationOfficeAllowedChange={setCommunicationOfficeAllowed}
          onRemoveCommunication={removeCommunication}
          onAddCommunication={addCommunication}
          canEditStandorte={canEditPerson}
          isEditingStandorte={isEditingStandorte}
          setIsEditingStandorte={setIsEditingStandorte}
          standorte={standorte}
          personStandorteIds={personStandorteIds}
          abgeleiteteStandorteIds={abgeleiteteStandorteIds}
          onToggleStandort={handleToggleStandort}
          onSaveStandorte={handleSaveStandorte}
          onCancelStandorteEdit={handleCancelStandorteEdit}
          onStartEditStammdaten={() => setIsEditing(true)}
          onSaveStammdaten={handleSave}
          onCancelEditStammdaten={() => {
            setIsEditing(false);
            setFormData(person);
          }}
          emailCheck={emailCheck}
          onAnrufen={(nummer) => void anrufenAusDatensatz(nummer)}
          onNachricht={(kanal) => setNachrichtKanal(kanal)}
        />

        <TabsContent value="customer">
          <div className="space-y-6">
            <PersonCollapsibleSection
              title="Einstellungen"
              description="Kundenbezogene Kommunikationsoptionen"
            >
              <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                <div>
                  <div className="font-medium">Werbemails</div>
                  <div className="text-sm text-muted-foreground">
                    Newsletter an {person.email || 'die E-Mail-Adresse dieses Datensatzes'}. Der Kunde kann das
                    auch selbst im Kundenbereich ändern.
                  </div>
                </div>
                <Switch
                  checked={werbemails === true}
                  disabled={werbemails === null || !canEditPerson}
                  onCheckedChange={(v) => void werbemailsSetzen(v === true)}
                  aria-label="Werbemails"
                />
              </div>
            </PersonCollapsibleSection>

            <PersonCollapsibleSection title="Verträge" description="Aktive, gekündigte und beendete Verträge" defaultOpen>
              <VertragsKacheln
                personId={person.id}
                reloadKey={vertraegeStand}
                onKuendigen={(enrollmentId) => {
                  setKuendigungTeilnahmeId(enrollmentId);
                  setKuendigungWizardOpen(true);
                }}
                auftragPdf={(enrollmentId) => {
                  const alt = contracts.find((c) => c.enrollmentId === enrollmentId);
                  return alt?.orderId && alt.hasContractPdf ? () => void openContractPdf(alt.orderId as number) : null;
                }}
              />
            </PersonCollapsibleSection>

            <PersonCollapsibleSection
              title="Kontingente & Zahlungen"
              description="Unterrichtseinheiten und Transaktionen dieses Kunden"
            >
                <div className="grid gap-6 md:grid-cols-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Verfügbare Einheiten</span>
                      <span className="text-2xl font-bold">{getTotalAvailableUnits()}</span>
                    </div>
                    <Progress value={getTotalAvailableUnits() / getTotalPurchasedUnits() * 100} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {getTotalAvailableUnits()} von {getTotalPurchasedUnits()} Einheiten verfügbar
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Verwendete Einheiten</span>
                      <span className="text-2xl font-bold">{getTotalUsedUnits()}</span>
                    </div>
                    <Progress value={getTotalUsedUnits() / getTotalPurchasedUnits() * 100} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {getTotalUsedUnits()} von {getTotalPurchasedUnits()} Einheiten verwendet
                    </p>
                  </div>
                  {canEditContingents && !contingentLedgerReadOnly && (
                    <div className="flex flex-col items-center justify-center">
                      <Button onClick={openNewContingent}>
                        <PlusCircle className="mr-2 h-4 w-4" />
                        Kontingent hinzufügen
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-6 grid gap-6 md:grid-cols-3">
                  <div className="md:col-span-2">
                    {contingents.length === 0 ? (
                      <div className="rounded-lg border p-8 text-center">
                        <Package className="mx-auto h-12 w-12 text-muted-foreground" />
                        <h2 className="mt-4 text-lg font-semibold">Keine Kontingente gefunden</h2>
                        <p className="mt-2 text-sm text-muted-foreground">Diese Person hat noch keine Unterrichtseinheiten gekauft.</p>
                        {canEditContingents && !contingentLedgerReadOnly && (
                          <Button className="mt-4" onClick={openNewContingent}>
                            <PlusCircle className="mr-2 h-4 w-4" /> Kontingent hinzufügen
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {contingents.map((c) => (
                          <div key={String(c.id)} className="rounded-lg border p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold">{c.name}</h3>
                                <div className="mt-1 text-sm text-muted-foreground">
                                  {c.purchasedAt ? `Gekauft am ${formatDate(c.purchasedAt)}` : 'Ohne Datum'}
                                </div>
                                {c.description && <div className="mt-1 text-sm text-muted-foreground">{c.description}</div>}
                              </div>
                              <Badge variant="outline" className="ml-2">{c.remainingUnits} / {c.totalUnits} Einheiten</Badge>
                            </div>
                            <div className="mt-3">
                              <Progress value={((c.totalUnits - c.remainingUnits) / (c.totalUnits || 1)) * 100} className="h-2" />
                              <div className="mt-1 text-xs text-muted-foreground">{c.totalUnits - c.remainingUnits} von {c.totalUnits} verwendet</div>
                            </div>
                            {canEditContingents && !contingentLedgerReadOnly && (
                              <div className="mt-3 flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => openEditContingent(c)}>
                                  <Edit className="mr-2 h-4 w-4" /> Bearbeiten
                                </Button>
                                <Button variant="outline" size="sm" className="text-destructive" onClick={() => deleteContingent(c)}>
                                  Löschen
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <PaymentHistoryTab
                      visible={activeTab === 'customer'}
                      wpUserId={Number(person.wpUserId || person.id) || 0}
                      embedded
                      onTerminate={(enrollmentId) => {
                        // Die Teilnahme-ID des Assistenten ist die der Anmeldung.
                        setKuendigungTeilnahmeId(enrollmentId);
                        setKuendigungWizardOpen(true);
                      }}
                    />
                  </div>
                </div>
            </PersonCollapsibleSection>

            <ContractAccountsSection personId={person.id} personName={person.name ?? ''} contracts={contracts} />

            <EinmalcodesSection personId={person.id} />

            <PersonAssociatedPersonsTab
              visible={showAssociatedPersonsTab}
              personId={person.id}
              subprofiles={subprofiles}
              onAdd={openAddSubprofile}
              onEdit={openEditSubprofile}
              onDelete={deleteSubprofile}
            />

            <Card>
              <CardHeader>
                <CardTitle>Historie</CardTitle>
                <CardDescription>Chronologische Aktivitäten zu diesem Kunden</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? <div className="rounded-lg border p-8 text-center">
                    <History className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-lg font-semibold">Keine Aktivitäten gefunden</h2>
                    <p className="mt-2 text-sm text-muted-foreground">Es wurden noch keine Aktivitäten für diese Person aufgezeichnet.</p>
                  </div> : <div className="space-y-4">
                    <div className="relative pl-6 before:absolute before:left-2.5 before:top-0 before:h-full before:w-px before:bg-muted">
                      {history.map((entry) => <div key={entry.id} className="relative mb-6 last:mb-0">
                          <div className="absolute -left-6 top-0.5 h-5 w-5 rounded-full border bg-background flex items-center justify-center">
                            {getHistoryIcon(entry.type)}
                          </div>
                          <div className="rounded-lg border p-4">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                              <div>
                                <p className="font-medium flex flex-wrap items-center gap-2">
                                  <span className={entry.correctedById ? 'line-through text-muted-foreground' : undefined}>{entry.description}</span>
                                  {entry.correctedById ? <Badge variant="outline">korrigiert</Badge> : null}
                                  {entry.correctsId ? <Badge variant="secondary">Korrektur</Badge> : null}
                                  <TaskCount personHistoryId={Number(entry.id)} />
                                </p>
                                {entry.correctsId && entry.correctionReason ? (
                                  <p className="mt-1 text-xs text-muted-foreground">Grund: {entry.correctionReason}</p>
                                ) : null}
                                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                                  <span>{formatDateTimeCentral(entry.date || entry.createdAt || new Date().toISOString())}</span>
                                  <span>•</span>
                                  {(entry.actorEmail || entry.user) && <span>Durchgeführt von: {entry.actorEmail || entry.user}</span>}
                                </div>
                              </div>
                              {can(P.personen.personen.personenhistorie_bearbeiten) && !entry.correctedById ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="mt-2 md:mt-0"
                                  onClick={() => setKorrekturFuer({ id: entry.id, description: entry.description, details: entry.details })}
                                >
                                  <FileEdit className="mr-1 h-4 w-4" /> Korrigieren
                                </Button>
                              ) : null}
                            </div>
                            {entry.details ? (
                              <pre className="mt-3 whitespace-pre-wrap break-words rounded border border-border/50 bg-muted/30 p-3 text-xs leading-5 font-sans text-muted-foreground">
                                {entry.details}
                              </pre>
                            ) : null}
                            <div className="mt-3">
                              <details>
                                <summary className="cursor-pointer text-sm text-muted-foreground">Aufgaben anzeigen</summary>
                                <div className="mt-3">
                                  <TaskList personHistoryId={Number(entry.id)} showCreate={true} />
                                </div>
                              </details>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </CardContent>
            </Card>
          </div>
          <PersonZusammenfuehrenDialog
            open={zusammenfuehrenOffen}
            zielId={String(person.id)}
            zielName={person.name}
            onClose={() => setZusammenfuehrenOffen(false)}
            onZusammengefuehrt={(bleibt) => {
              setZusammenfuehrenOffen(false);
              if (String(bleibt) === String(person.id)) {
                window.location.reload();
              } else {
                navigate(`${peopleListPath}/${bleibt}`);
              }
            }}
          />
          <HistoryCorrectionDialog
            personId={String(id || '')}
            eintrag={korrekturFuer}
            onClose={() => setKorrekturFuer(null)}
            onKorrigiert={() => void historieNeuLaden()}
          />
          {/* Admin-Dialog Kontingente */}
          <WizardShell
            open={!contingentLedgerReadOnly && isContDialogOpen}
            onOpenChange={setIsContDialogOpen}
            title={editingCont ? 'Kontingent bearbeiten' : 'Kontingent anlegen'}
            description="Verwalten Sie die Kontingente dieses Kunden"
            contentClassName="w-[1080px] h-[780px] max-w-[95vw] max-h-[95vh]"
            bodyClassName="py-4"
            onCancel={() => setIsContDialogOpen(false)}
            cancelLabel="Abbrechen"
            onPrimary={saveContingent}
            primaryLabel="Speichern"
            canPrimary={true}
          >
            <div className="grid gap-4">
              <div>
                <label htmlFor="cont-name" className="text-sm font-medium">Name</label>
                <Input id="cont-name" value={contForm.name} onChange={e => setContForm(prev => ({ ...prev, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="cont-total-units" className="text-sm font-medium">Gesamteinheiten</label>
                  <Input id="cont-total-units" type="number" min={1} value={contForm.totalUnits} onChange={e => setContForm(prev => ({ ...prev, totalUnits: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="cont-remaining-units" className="text-sm font-medium">Verfügbare Einheiten</label>
                  <Input id="cont-remaining-units" type="number" min={0} value={contForm.remainingUnits} onChange={e => setContForm(prev => ({ ...prev, remainingUnits: e.target.value }))} />
                </div>
              </div>
              <div>
                <label htmlFor="cont-description" className="text-sm font-medium">Beschreibung</label>
                <Textarea id="cont-description" rows={3} value={contForm.description} onChange={e => setContForm(prev => ({ ...prev, description: e.target.value }))} />
              </div>
            </div>
          </WizardShell>
        </TabsContent>

        <PersonTeacherTab
          visible={showTeacherTab}
          teacherEditing={teacherEditing}
          onStartEdit={() => setTeacherEditing(true)}
          onCancelEdit={handleTeacherCancelEdit}
          onSaveTeacher={handleTeacherSave}
          teacherForm={teacherForm}
          setTeacherForm={setTeacherForm}
          subjectOptions={subjectOptions}
          teachingArts={teachingArts}
          teacherArtRates={teacherArtRates}
          setTeacherArtRates={setTeacherArtRates}
          personStandorteIds={personStandorteIds}
          standorte={standorte}
          availabilityStandortId={availabilityStandortId}
          setAvailabilityStandortId={setAvailabilityStandortId}
          availabilityEditing={availabilityEditing}
          setAvailabilityEditing={setAvailabilityEditing}
          availabilityLoading={availabilityLoading}
          onReloadAvailability={handleAvailabilityReload}
          onSaveAvailability={handleAvailabilitySave}
          availabilitySlots={availabilitySlots}
          daysOfWeek={daysOfWeek}
          addSlotForDay={addSlotForDay}
          setSlotTime={setSlotTime}
          removeSlot={removeSlot}
          wpUserId={Number(person.wpUserId || person.id)}
          roleActive={((person as unknown as { teacher?: { roleActive?: boolean } }).teacher)?.roleActive !== false}
          onToggleRoleActive={(aktiv) => void handleTeacherRoleActive(aktiv)}
        />
      </Tabs>

      <PersonDetailsWizardModal
        open={detailsModalOpen}
        onOpenChange={setDetailsModalOpen}
        personId={person.id}
        onUpdated={(updated) => {
          setPerson((prev) => (prev ? ({ ...prev, ...updated } as Person) : prev));
        }}
        onDeleted={() => {
          setDetailsModalOpen(false);
          navigate(peopleListPath);
        }}
      />

      {/* Zugang sperren / freischalten */}
      <Dialog open={lockConfirmOpen} onOpenChange={(v) => { if (!v && !isAccessUpdating) setLockConfirmOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Zugang sperren</DialogTitle>
            <DialogDescription>
              Der Kunde kann sich danach nicht mehr im Portal anmelden. Bestehende API-Zugriffe über das Portal werden blockiert.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setLockConfirmOpen(false)} disabled={isAccessUpdating}>
              Abbrechen
            </Button>
            <Button type="button" variant="destructive" onClick={handleLockAccess} disabled={isAccessUpdating} loading={isAccessUpdating}>
              Zugang sperren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unlockConfirmOpen} onOpenChange={(v) => { if (!v && !isAccessUpdating) setUnlockConfirmOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Zugang freischalten</DialogTitle>
            <DialogDescription>
              Der Kunde kann sich wieder im Portal anmelden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setUnlockConfirmOpen(false)} disabled={isAccessUpdating}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleUnlockAccess} disabled={isAccessUpdating} loading={isAccessUpdating}>
              Freischalten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subprofil-Dialog */}
      <SubProfileForm
        parent={person}
        isOpen={isSubFormOpen}
        onClose={() => setIsSubFormOpen(false)}
        onSave={saveSubprofile}
        initialData={editingSubIndex !== null ? subprofiles[editingSubIndex] : null}
      />
      {nachrichtKanal ? (
        <MessageWizardModal
          open
          onOpenChange={(o) => {
            if (!o) setNachrichtKanal(null);
          }}
          standortId={standortFuerKontakt}
          title={nachrichtKanal === 'sms' ? 'SMS schreiben' : 'E-Mail schreiben'}
          recipients={[
            {
              id: `person-${person.id}`,
              type: 'person',
              wpUserId: Number(person.wpUserId || person.id),
              name: person.name || `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim(),
              email: person.email ?? null,
              phone: person.phone ?? null,
            },
          ]}
          initialChannel={nachrichtKanal}
          context={{ entityType: 'person', entityId: Number(person.id) }}
          onSent={() => {
            setNachrichtKanal(null);
            void historieNeuLaden();
          }}
        />
      ) : null}
      {/* Kündigung Wizard */}
      {kuendigungTeilnahmeId > 0 && (
        <KuendigungWizardModal
          open={kuendigungWizardOpen}
          onOpenChange={setKuendigungWizardOpen}
          teilnahmeId={kuendigungTeilnahmeId}
          standortId={selectedStandortId || 0}
          onCreated={() => {
            setKuendigungWizardOpen(false);
            setKuendigungTeilnahmeId(0);
            setVertraegeStand((n) => n + 1);
          }}
        />
      )}
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={
          zuLoeschen?.art === 'kontingent'
            ? `Kontingent „${zuLoeschen.kontingent.name}“ löschen?`
            : `Zugeordnete Person „${zuLoeschen?.art === 'profil' ? zuLoeschen.name : ''}“ löschen?`
        }
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen?.art === 'profil') await subprofilLoeschen(zuLoeschen.index, grund);
          else if (zuLoeschen?.art === 'kontingent') await kontingentLoeschen(zuLoeschen.kontingent, grund);
        }}
      />
    </div>;
};
export default PersonDetail;
